<template>
  <div
    v-if="visibleItems.length"
    ref="bar"
    class="c-navigation-bar md:hidden w-full sticky bottom-0 z-20"
  >
    <anchor-scrollactive
      :modify-url="true"
      :offset="activationOffset"
      :scroll-offset="scrollOffset"
      :scroll-on-start="false"
      active-class="is-active"
      tag="ul"
      class="c-navigation-bar__list flex bg-blue-dark text-sm text-white overflow-x-auto whitespace-no-wrap"
    >
      <li
        v-for="item in visibleItems"
        :key="item.href"
        class="flex-none px-4"
      >
        <a
          :href="item.href"
          class="block pt-4 pb-5 border-t-4 border-blue-dark scrollactive-item"
          @click="trackClick(item)"
        >
          {{ item.title }}
        </a>
      </li>
    </anchor-scrollactive>
  </div>
</template>

<script>
import {
  ref, nextTick, onMounted, onUnmounted
} from 'vue'
import VueScrollActive from 'vue-scrollactive'
import tracking from '@/helpers/tracking'

// A section becomes active this many px before its top reaches the top of the viewport.
// Not "exact" on purpose: sections are nested (nutrition sits inside product information), and
// exact mode would flash the outer section between the end of the inner one and the next section.
const ACTIVATION_OFFSET = 80
// Gap left above a section after clicking its link. Must be truthy, as scrollactive falls back to
// the activation offset otherwise.
const SCROLL_OFFSET = 16
// scrollactive's own jump to the URL hash on load ("scroll-on-start") positions nested sections
// by their offsetTop relative to the positioned parent, so the browser's native hash jump is used instead.

// Set on <body> while the bar is visible, so the chat launcher can be lifted above it.
const BODY_CLASS = 'has-anchor-nav-bar'
// Rendered height of the bar, used to place the chat launcher right above it (see anchor-nav-bar.css).
const HEIGHT_PROPERTY = '--anchor-nav-bar-height'

// The plugin only exposes the component through install(), so grab it to extend it.
let Scrollactive
VueScrollActive.install({ component: (name, component) => { Scrollactive = component } })

// When the page is scrolled to the bottom, the last sections can be too short to ever reach the
// activation line, so the last section visible above the bar is activated instead.
const AnchorScrollactive = {
  extends: Scrollactive,
  methods: {
    getItemInsideWindow() {
      const distanceFromTop = window.pageYOffset
      const isAtBottom = distanceFromTop + window.innerHeight >= document.documentElement.scrollHeight - 1

      if (!isAtBottom) {
        return Scrollactive.methods.getItemInsideWindow.call(this)
      }

      const visibleBottom = distanceFromTop + window.innerHeight - this.$el.offsetHeight

      // items are the .scrollactive-item links, in page order
      return Array.from(this.items)
        .filter((item) => {
          const section = document.getElementById(decodeURI(item.hash.substr(1)))
          return section && this.getOffsetTop(section) < visibleBottom
        })
        .pop()
    },

    // scrollactive animates the scroll itself with a scrollTo() per frame. With the site wide
    // `scroll-behavior: smooth` every one of those starts a native smooth scroll that is still running
    // when the scroll listener is re-attached, so links of the sections scrolled past flash active.
    scrollTo(target) {
      const root = document.documentElement
      const previousScrollBehavior = root.style.scrollBehavior
      root.style.scrollBehavior = 'auto'

      return Scrollactive.methods.scrollTo.call(this, target).finally(() => {
        root.style.scrollBehavior = previousScrollBehavior
      })
    }
  }
}

const targetExists = (href) => {
  if (!href || href[0] !== '#') {
    return false
  }

  return Boolean(document.getElementById(href.slice(1)))
}

export default {
  components: {
    AnchorScrollactive
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    // Name of the block the anchors are placed in, used for tracking.
    placement: {
      type: String,
      default: 'navigation'
    }
  },
  setup(props) {
    // Sections are optional (no related recipes, no FAQ block, ...), so only keep
    // the anchors that actually have a target on the rendered page.
    const visibleItems = ref([])
    const bar = ref(null)

    const updateHeight = () => {
      if (bar.value) {
        document.body.style.setProperty(HEIGHT_PROPERTY, `${bar.value.offsetHeight}px`)
      }
    }

    const trackClick = (item) => {
      tracking.anchorClick({
        anchorTitle: item.title,
        placement: props.placement
      })
    }

    onMounted(() => {
      visibleItems.value = props.items.filter((item) => item.title && targetExists(item.href))

      if (visibleItems.value.length) {
        document.body.classList.add(BODY_CLASS)
        nextTick(updateHeight)
        window.addEventListener('resize', updateHeight)
      }
    })

    onUnmounted(() => {
      document.body.classList.remove(BODY_CLASS)
      document.body.style.removeProperty(HEIGHT_PROPERTY)
      window.removeEventListener('resize', updateHeight)
    })

    return {
      visibleItems,
      bar,
      activationOffset: ACTIVATION_OFFSET,
      scrollOffset: SCROLL_OFFSET,
      trackClick
    }
  }
}
</script>
<style>
  .c-navigation-bar .is-active {
    @apply font-bold border-white;
  }

  .c-navigation-bar__list {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .c-navigation-bar__list::-webkit-scrollbar {
    display: none;
  }
</style>
